import { useRef } from 'react';

import classNames from 'classnames';

import { RecapAsset } from '@/api/trading/types';
import { AssetIcon } from '@/blocks/components/AssetIcon';
import { DEFAULT_SPARKLINE_MINI_CHART_WIDTH, SparklineChart } from '@/blocks/components/SparklineChart';
import { IconPriceTrend } from '@/blocks/components/Table/IconPriceTrend/IconPriceTrend';
import { WithTrendColor } from '@/blocks/components/WithTrendColor';
import { FormatAssetName, FormatPercent, FormatPrice } from '@/blocks/formatters';
import { extractAssetName } from '@/blocks/formatters/format-asset-name';
import { LinkI18n } from '@/i18n/routing';
import typography from '@/styles/typography.module.scss';
import { Optional } from '@/types';
import { extractNumber } from '@/utils/number';

import styles from '@/app/[lang]/price-chart/[category]/TickersTape/styles.module.scss';
import { getAssetPageLink } from '@/app/[lang]/price-chart/[category]/navigation-utils';

export function TickersTapeCard(props: { asset: Optional<RecapAsset, keyof RecapAsset> & { name: string } }) {
  const ref = useRef<HTMLDivElement | null>(null);

  return (
    <LinkI18n
      href={getAssetPageLink(props.asset.name)}
      className={classNames(styles.tickersTapeCard)}
      data-event-label={extractAssetName(props.asset)}
    >
      <div className={classNames(styles.nameAndPrice, typography.subheadingSM)}>
        <div>
          <AssetIcon assetName={props.asset.name} loading={'eager'} />
          <FormatAssetName assetName={extractAssetName(props.asset)} />
        </div>
        <WithTrendColor negative={extractNumber(props.asset.change24h) < 0}>
          <FormatPrice
            price={extractNumber(props.asset.price)}
            priceScale={extractNumber(props.asset.priceScale)}
          />
        </WithTrendColor>
      </div>
      <div className={styles.change}>
        <IconPriceTrend negative={extractNumber(props.asset.change24h) < 0} />
        <WithTrendColor negative={extractNumber(props.asset.change24h) < 0}>
          (<FormatPercent value={extractNumber(props.asset.change24h)} forcePlusSign={true} />)
        </WithTrendColor>
      </div>
      <div className={styles.trend} ref={ref}>
        <SparklineChart
          chartValues={props.asset.trend || []}
          negative={extractNumber(props.asset.change24h) < 0}
          width={ref?.current?.clientWidth || DEFAULT_SPARKLINE_MINI_CHART_WIDTH}
          height={34}
        />
      </div>
    </LinkI18n>
  );
}
